import { cn } from '@commenda/utils';
import { Row } from '@tanstack/react-table';
import React from 'react';

import {
  TableBody as TBody,
  TableCell as TCell,
  TableRow as TRow,
} from '../../Table';
import { DataGridCellLineItem } from '../cells';
import {
  DATA_GRID_DEFAULT_COLLAPSE_LABEL,
  DATA_GRID_DEFAULT_COLUMN_WIDTH,
  DATA_GRID_DEFAULT_EXPAND_LABEL,
  DATA_GRID_INDENT_STEP,
  DATA_GRID_ROW_HEADER_COLUMN_ID,
  DATA_GRID_ROW_HEADER_WIDTH,
} from '../constants';
import {
  DataGridAlign,
  DataGridClassNames,
  DataGridColumn,
  DataGridExpandState,
  DataGridExpansionConfig,
  DataGridRowHeaderConfig,
} from '../types';
import {
  getDataGridColumnMinWidthStyle,
  getDataGridJustifyClass,
} from '../utils';
import {
  DATA_GRID_BODY_CELL_CLASS_NAMES,
  DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
  DATA_GRID_PINNED_SHADOW_CLASS_NAMES,
  DATA_GRID_ROW_HEADER_CELL_CLASS_NAMES,
  DATA_GRID_ROW_SEPARATOR_CLASS_NAMES,
  DATA_GRID_STICKY_ROW_HEADER_CLASS_NAMES,
} from './classNames';

export interface DataGridBodyProps<TRow> {
  rows: Row<TRow>[];
  rowHeaderConfig: DataGridRowHeaderConfig<TRow>;
  leafColumnsById: Map<string, DataGridColumn<TRow>>;
  expansionConfig?: DataGridExpansionConfig;
  isRowHeaderPinned: boolean;
  showPinnedColumnShadow: boolean;
  onRowClick?: (row: TRow) => void;
  classNames?: DataGridClassNames;
}

const getExpandState = <TRow,>(row: Row<TRow>): DataGridExpandState => {
  if (!row.getCanExpand()) {
    return 'none';
  }
  return row.getIsExpanded() ? 'expanded' : 'collapsed';
};

const onRowKeyDown = (
  event: React.KeyboardEvent<HTMLTableRowElement>,
  activate: () => void
) => {
  if (event.target !== event.currentTarget) {
    return;
  }
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  activate();
};

export function DataGridBody<TRow>({
  rows,
  rowHeaderConfig,
  leafColumnsById,
  expansionConfig,
  isRowHeaderPinned,
  showPinnedColumnShadow,
  onRowClick,
  classNames,
}: DataGridBodyProps<TRow>) {
  return (
    <TBody className={cn('divide-y-0', classNames?.body)}>
      {rows.map((row) => {
        const expandState = getExpandState(row);
        const activate = onRowClick
          ? () => onRowClick(row.original)
          : undefined;

        return (
          <TRow
            key={row.id}
            className={cn(onRowClick && 'cursor-pointer', classNames?.row)}
            onClick={activate}
            onKeyDown={
              activate ? (event) => onRowKeyDown(event, activate) : undefined
            }
            tabIndex={activate ? 0 : undefined}
          >
            <TCell
              style={getDataGridColumnMinWidthStyle(
                rowHeaderConfig.width ?? DATA_GRID_ROW_HEADER_WIDTH
              )}
              className={cn(
                DATA_GRID_ROW_HEADER_CELL_CLASS_NAMES,
                DATA_GRID_ROW_SEPARATOR_CLASS_NAMES,
                DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
                isRowHeaderPinned && DATA_GRID_STICKY_ROW_HEADER_CLASS_NAMES,
                isRowHeaderPinned &&
                  showPinnedColumnShadow &&
                  DATA_GRID_PINNED_SHADOW_CLASS_NAMES,
                classNames?.rowHeaderCell
              )}
            >
              <DataGridCellLineItem
                depth={row.depth}
                indentStep={rowHeaderConfig.indentStep ?? DATA_GRID_INDENT_STEP}
                expandState={expandState}
                onToggle={() => row.toggleExpanded()}
                ariaLabel={
                  expandState === 'expanded'
                    ? (expansionConfig?.ariaLabelCollapse ??
                      DATA_GRID_DEFAULT_COLLAPSE_LABEL)
                    : (expansionConfig?.ariaLabelExpand ??
                      DATA_GRID_DEFAULT_EXPAND_LABEL)
                }
              >
                {rowHeaderConfig.label(row.original)}
              </DataGridCellLineItem>
            </TCell>

            {row
              .getVisibleCells()
              .filter(
                (cell) => cell.column.id !== DATA_GRID_ROW_HEADER_COLUMN_ID
              )
              .map((cell) => {
                const leafColumn = leafColumnsById.get(cell.column.id);
                const align: DataGridAlign = leafColumn?.align ?? 'right';

                return (
                  <TCell
                    key={cell.id}
                    style={getDataGridColumnMinWidthStyle(
                      leafColumn?.width ?? DATA_GRID_DEFAULT_COLUMN_WIDTH
                    )}
                    className={cn(
                      DATA_GRID_BODY_CELL_CLASS_NAMES,
                      DATA_GRID_ROW_SEPARATOR_CLASS_NAMES,
                      DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
                      classNames?.cell
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center',
                        getDataGridJustifyClass(align)
                      )}
                    >
                      {leafColumn?.cell(row.original)}
                    </div>
                  </TCell>
                );
              })}
          </TRow>
        );
      })}
    </TBody>
  );
}
